class MultiTabColumns extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('.multi-tab-columns__tab');
    this.panels = this.querySelectorAll('.multi-tab-columns__panel');
    this.activeTabIndex = 0;
    
    this.init();
  }

  init() {
    if (this.tabs.length === 0 || this.panels.length === 0) return;
    
    this.setupEventListeners();
    this.setupKeyboardNavigation();
    this.setInitialState();
  }

  setupEventListeners() {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        this.activateTab(index);
      });
    });
  }

  setupKeyboardNavigation() {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('keydown', (event) => {
        this.handleKeydown(event, index);
      });
    });
  }

  handleKeydown(event, currentIndex) {
    const { key } = event;
    let newIndex = currentIndex;

    switch (key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        newIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = this.tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.activateTab(currentIndex);
        return;
      default:
        return;
    }

    if (newIndex !== currentIndex) {
      this.tabs[newIndex].focus();
    }
  }

  activateTab(index) {
    if (index < 0 || index >= this.tabs.length) return;
    
    // Update tab states
    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.classList.toggle('multi-tab-columns__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive.toString());
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // Update panel states
    this.panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === index;
      panel.classList.toggle('multi-tab-columns__panel--active', isActive);
      
      if (isActive) {
        panel.removeAttribute('hidden');
        // Trigger any animations or lazy loading for the active panel
        this.triggerPanelActivation(panel);
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    this.activeTabIndex = index;
    
    // Dispatch custom event for any additional functionality
    this.dispatchEvent(new CustomEvent('tab-changed', {
      detail: {
        activeIndex: index,
        activeTab: this.tabs[index],
        activePanel: this.panels[index]
      }
    }));
  }

  triggerPanelActivation(panel) {
    // Trigger any slider components within the panel
    const sliderComponents = panel.querySelectorAll('slider-component');
    sliderComponents.forEach(slider => {
      if (slider.initSlider) {
        slider.initSlider();
      }
    });

    // Trigger any lazy loading or animations
    const lazyElements = panel.querySelectorAll('[data-lazy]');
    lazyElements.forEach(element => {
      if (element.load) {
        element.load();
      }
    });
  }

  setInitialState() {
    // Set initial tabindex values
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('tabindex', index === 0 ? '0' : '-1');
    });

    // Ensure first tab is active
    this.activateTab(0);
  }

  // Public method to programmatically switch tabs
  switchToTab(index) {
    this.activateTab(index);
  }

  // Public method to get current active tab index
  getActiveTabIndex() {
    return this.activeTabIndex;
  }

  // Public method to get current active tab element
  getActiveTab() {
    return this.tabs[this.activeTabIndex];
  }

  // Public method to get current active panel element
  getActivePanel() {
    return this.panels[this.activeTabIndex];
  }
}

// Register the custom element
customElements.define('multi-tab-columns', MultiTabColumns);

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MultiTabColumns;
}
